import type React from "react"
import { useEffect, useRef, useCallback } from "react"
import { useAppDispatch, useAppSelector } from "@/app/hooks"
import {
  fetchReviews,
  selectReviews,
  selectReviewsStatus,
  selectReviewsHasMore,
  resetReviews,
} from "@/features/siteReviews/siteReviewsSlice"
import type { ReviewResponse } from "@/types/review"
import { ReviewItem } from "./ReviewItem"
import "./ReviewList.css"

type ReviewListProps = {
  siteId: string
  onEdit: (review: ReviewResponse) => void
}

export const ReviewList: React.FC<ReviewListProps> = ({ siteId, onEdit }) => {
  const dispatch = useAppDispatch()
  const reviews = useAppSelector(selectReviews)
  const status = useAppSelector(selectReviewsStatus)
  const hasMore = useAppSelector(selectReviewsHasMore)
  const page = useAppSelector(state => state.siteReviews.page)
  
  const observer = useRef<IntersectionObserver | null>(null)

  // Reset and fetch when siteId changes
  useEffect(() => {
    void dispatch(resetReviews())
    void dispatch(fetchReviews({ siteId, page: 0 }))
  }, [dispatch, siteId])

  const lastReviewElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (status === "loading") return
      if (observer.current) observer.current.disconnect()
      if (!node) return
      
      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMore) {
          void dispatch(fetchReviews({ siteId, page })) // fetch next page
        }
      })
      
      observer.current.observe(node)
    },
    [status, hasMore, dispatch, siteId, page],
  )

  return (
    <div className="review-list-container">
      {reviews.map((review, index) => {
        if (reviews.length === index + 1) {
          return (
            <div ref={lastReviewElementRef} key={`${review.id}-${index.toString()}`}>
              <ReviewItem review={review} onEdit={onEdit} />
            </div>
          )
        } else {
          return <ReviewItem key={`${review.id}-${index.toString()}`} review={review} onEdit={onEdit} />
        }
      })}
      
      {status === "loading" && (
        <div className="review-list-loading">
          <div className="loading-spinner"></div>
        </div>
      )}
      
      {status === "succeeded" && reviews.length === 0 && (
          <div className="review-list-empty">
              등록된 리뷰가 없습니다.<br />
              첫 번째 리뷰를 남겨보세요!
          </div>
      )}
      
       {status === "failed" && (
          <div className="review-list-error">
              리뷰를 불러오는 중 오류가 발생했습니다.
          </div>
      )}
    </div>
  )
}
